import { ICommonSettings, IElasticApmSetting } from "@kuika/kuika-cl-model";
import { EElasticApmCaptureBody, EElasticApmLogLevel } from "@kuika/kuika-cl-model/src/models/common-config";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter, ConfigModalGroupHeading } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const ELASTIC_APM_SETTINGS_SEARCH_TERMS: string[] = [
  "Server URL",
  "Service Name",
  "Secret Token",
  "Environment",
  "Enable Frontend Tracing",
  "Advanced",
  "Transaction Sample Rate",
  "Span Frames Min Duration (ms)",
  "Capture Body",
  "Transaction Max Spans",
  "Log Level",
  "Stack Trace Limit",
  "Metrics Interval (seconds)",
  "Capture Headers"
];

export interface IConfigElasticApmSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IElasticApmSetting;
}

export interface IConfigElasticApmSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingElasticApmId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigElasticApmSettings extends React.PureComponent<
  IConfigElasticApmSettingsProps,
  IConfigElasticApmSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigElasticApmSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigElasticApmSettingsProps & IWithSDProps) {
    if (prevProps.editingElasticApmId !== this.props.editingElasticApmId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingElasticApmId) return;

    const editingConfig = this.props.commonSettings.elasticApmSettings?.find(
      (x) => x.id === this.props.editingElasticApmId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const elasticApmSettings = this.props.commonSettings?.elasticApmSettings;
    if (!elasticApmSettings) return;

    const isSettingAlreadyExist =
      elasticApmSettings.some((x) => x.name === value && x.id !== this.props.editingElasticApmId) ||
      value === "Default";

    if (isSettingAlreadyExist) throw new Error("Elastic APM Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IElasticApmSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      // Connection Settings
      serverUrl: data.serverUrl?.trim(),
      serviceName: data.serviceName?.trim(),
      secretToken: data.secretToken?.trim() || undefined,
      environment: data.environment?.trim() || undefined,
      // Transaction/Span Settings
      transactionSampleRate: data.transactionSampleRate,
      spanFramesMinDuration: data.spanFramesMinDuration,
      captureBody: data.captureBody,
      transactionMaxSpans: data.transactionMaxSpans,
      // Error/Logging Settings
      logLevel: data.logLevel,
      stackTraceLimit: data.stackTraceLimit,
      // Performance Settings
      metricsInterval: data.metricsInterval,
      captureHeaders: data.captureHeaders,
      // Frontend Settings
      enableFrontendTracing: data.enableFrontendTracing
    };
    if (this.props.editingElasticApmId) {
      obj.id = this.props.editingElasticApmId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "elasticApmSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "elasticApmSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingElasticApmId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingElasticApmId || "",
      configKey: "elasticApmSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Elastic APM setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Elastic APM setting"
        secondHeader="Elastic APM setting"
        width={700}
        centered={true}
        bodyStyle={{ padding: 0, maxHeight: "70vh", overflowY: "auto" }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingElasticApmId ? this.deleteSetting : undefined}
            deleteTestId="elasticApmSettingDeleteButton"
            submitTestId="elasticApmSettingCreateButton"
            submitLabel={this.props.editingElasticApmId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configElasticApmForm"
          />
        }
      >
        <Form
          id="configElasticApmForm"
          name="elasticApmSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
          initialValues={{
            transactionSampleRate: 1.0,
            spanFramesMinDuration: 5,
            captureBody: EElasticApmCaptureBody.Off,
            transactionMaxSpans: 500,
            logLevel: EElasticApmLogLevel.Info,
            stackTraceLimit: 50,
            metricsInterval: 30,
            captureHeaders: true,
            enableFrontendTracing: true
          }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_elasticapmsettingspop_up_description")}
          >
            {/* CONNECTION SETTINGS */}
            <ConfigField label="Name" required tooltipContentKey="appsettings_elasticapmsettingspop_up_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="elasticApmSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Server URL" required tooltipContentKey="appsettings_elasticapmsettingspop_up_serverurl">
              <Form.Item
                name="serverUrl"
                rules={[
                  { required: true, message: "Please enter the APM Server URL." },
                  {
                    pattern:
                      /^https?:\/\/((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6})|(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}))(:\d{1,5})?(\/\S*)?(\?\S*)?$/,
                    message: "Server URL is not valid"
                  }
                ]}
              >
                <KuikaInput
                  size="md"
                  data-testid="elasticApmSettingServerUrlInput"
                  placeholder="https://your-apm-server:8200"
                />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Service Name"
              required
              tooltipContentKey="appsettings_elasticapmsettingspop_up_servicename"
            >
              <Form.Item name="serviceName" rules={[{ required: true, message: "Please enter the Service Name." }]}>
                <KuikaInput size="md" data-testid="elasticApmSettingServiceNameInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Secret Token" tooltipContentKey="appsettings_elasticapmsettingspop_up_secrettoken">
              <Form.Item name="secretToken">
                <KuikaInput size="md" type="password" />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Environment" tooltipContentKey="appsettings_elasticapmsettingspop_up_environment">
              <Form.Item name="environment">
                <KuikaInput size="md" placeholder="production, staging, development" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Enable Frontend Tracing"
              tooltipContentKey="appsettings_elasticapmsettingspop_up_enablefrontendtracing"
            >
              <Form.Item name="enableFrontendTracing" valuePropName="checked">
                <KuikaSwitch />
              </Form.Item>
            </ConfigField>

            {/* ADVANCED SETTINGS - Collapsible */}
            <KuikaCollapsePanel
              animated
              className="kuika-config-modal__collapse"
              // The chevron on the right leaves the heading on the body's own left edge,
              // in line with every field label above it.
              iconPlacement="right"
              // Every field stays mounted whether the group is open or not: the dialog reads
              // the whole form on submit, and a lazily-rendered panel would hand back nothing
              // for the settings the user never expanded.
              forceRender
              items={[
                {
                  key: "advanced",
                  label: <ConfigModalGroupHeading inline>Advanced</ConfigModalGroupHeading>,
                  children: (
                    <>
                      {/* TRANSACTION/SPAN SETTINGS */}
                      <ConfigModalGroupHeading>TRANSACTION/SPAN SETTINGS</ConfigModalGroupHeading>
                      <ConfigField
                        label="Transaction Sample Rate"
                        tooltipContentKey="appsettings_elasticapm_transactionsamplerate"
                      >
                        <Form.Item name="transactionSampleRate">
                          <KuikaInput size="md" type="number" min={0} max={1} step={0.1} placeholder="1.0" />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField
                        label="Span Frames Min Duration (ms)"
                        tooltipContentKey="appsettings_elasticapm_spanframesminduration"
                      >
                        <Form.Item name="spanFramesMinDuration">
                          <KuikaInput size="md" type="number" min={0} placeholder="5" />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Capture Body" tooltipContentKey="appsettings_elasticapm_capturebody">
                        <Form.Item name="captureBody">
                          <KuikaSelectbox
                            size="md"
                            options={[
                              { value: EElasticApmCaptureBody.Off, label: "Off" },
                              { value: EElasticApmCaptureBody.Errors, label: "Errors" },
                              { value: EElasticApmCaptureBody.Transactions, label: "Transactions" },
                              { value: EElasticApmCaptureBody.All, label: "All" }
                            ]}
                          />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField
                        label="Transaction Max Spans"
                        tooltipContentKey="appsettings_elasticapm_transactionmaxspans"
                      >
                        <Form.Item name="transactionMaxSpans">
                          <KuikaInput size="md" type="number" min={0} placeholder="500" />
                        </Form.Item>
                      </ConfigField>

                      {/* ERROR/LOGGING SETTINGS */}
                      <ConfigModalGroupHeading>ERROR/LOGGING SETTINGS</ConfigModalGroupHeading>
                      <ConfigField label="Log Level" tooltipContentKey="appsettings_elasticapm_loglevel">
                        <Form.Item name="logLevel">
                          <KuikaSelectbox
                            size="md"
                            options={[
                              { value: EElasticApmLogLevel.Trace, label: "Trace" },
                              { value: EElasticApmLogLevel.Debug, label: "Debug" },
                              { value: EElasticApmLogLevel.Info, label: "Info" },
                              { value: EElasticApmLogLevel.Warning, label: "Warning" },
                              { value: EElasticApmLogLevel.Error, label: "Error" },
                              { value: EElasticApmLogLevel.Critical, label: "Critical" },
                              { value: EElasticApmLogLevel.Off, label: "Off" }
                            ]}
                          />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Stack Trace Limit" tooltipContentKey="appsettings_elasticapm_stacktracelimit">
                        <Form.Item name="stackTraceLimit">
                          <KuikaInput size="md" type="number" min={0} placeholder="50" />
                        </Form.Item>
                      </ConfigField>

                      {/* PERFORMANCE SETTINGS */}
                      <ConfigModalGroupHeading>PERFORMANCE SETTINGS</ConfigModalGroupHeading>
                      <ConfigField
                        label="Metrics Interval (seconds)"
                        tooltipContentKey="appsettings_elasticapm_metricsinterval"
                      >
                        <Form.Item name="metricsInterval">
                          <KuikaInput size="md" type="number" min={0} placeholder="30" />
                        </Form.Item>
                      </ConfigField>
                      <ConfigField label="Capture Headers" tooltipContentKey="appsettings_elasticapm_captureheaders">
                        <Form.Item name="captureHeaders" valuePropName="checked">
                          <KuikaSwitch />
                        </Form.Item>
                      </ConfigField>
                    </>
                  )
                }
              ]}
            />
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
